"use client"

import React, { useState, useEffect, useRef } from "react"
import {
    Bar, BarChart, CartesianGrid, LabelList,
    XAxis, YAxis, Legend, Tooltip, ResponsiveContainer
} from "recharts"
import { CircleHelp, Download, ChevronDown } from "lucide-react"
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import axiosInstance from "@/api/axios"
import { ChartContainer } from "@/components/ui/chart"
import { Button } from "@/components/ui/button"
import {
    DropdownMenu, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "react-i18next"

// ── Types ────────────────────────────────────────────────────────────────────

interface WasteStreamEntry {
    waste_stream: string
    contamination_rate: number
    contaminated_items: number
    total_items: number
    contaminated_weight_kg: number
    total_weight_kg: number
    contamination_rate_by_weight: number
    contaminated_subclasses?: { name: string; count: number; weight_kg?: number }[]
}

interface PerBinData {
    bin: string
    total_items: number
    contaminated_items: number
    contamination_rate: number
    total_weight_kg: number
    contaminated_weight_kg: number
    contamination_rate_by_weight: number
    waste_streams: WasteStreamEntry[]
}

interface ApiResponse {
    per_bin_data: PerBinData[]
    contamination_rates: WasteStreamEntry[]
    overall_contamination_rate: number
    total_weight_kg: number
    total_contaminated_weight_kg: number
    overall_contamination_rate_by_weight: number
}

interface ContaminationRateChartProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string
    title?: string
    bins?: string[]
    timeRange?: string
    customDateRange?: Array<{
        startDate: Date | null
        endDate: Date | null
        key: string
    }>
    isCustomDateRange?: boolean
}

// ── Constants ────────────────────────────────────────────────────────────────

const STREAM_COLORS: Record<string, string> = {
    Organic:  "#A3176E",
    PMD:      "#436DBC",
    Paper:    "#E4981B",
    Residual: "#E53935",
}

const STREAMS = ["Organic", "PMD", "Paper", "Residual"] as const
type Stream = typeof STREAMS[number]

// Normalise API waste_stream strings to display names
const normalise = (ws: string): Stream | null => {
    const l = ws.toLowerCase()
    if (l === "organic")               return "Organic"
    if (l === "pmd" || l === "plastic") return "PMD"
    if (l === "paper")                 return "Paper"
    if (l === "residual" || l === "rest") return "Residual"
    return null
}

// ── Component ────────────────────────────────────────────────────────────────

export function ContaminationRateChart({
    className,
    title,
    bins = [],
    timeRange,
    customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
    isCustomDateRange = false,
    ...props
}: ContaminationRateChartProps) {
    const exportRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const [apiData, setApiData]   = useState<ApiResponse | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError]       = useState<string | null>(null)
    const [showKg, setShowKg]     = useState(false)   // false = %, true = kg

    const chartConfig = React.useMemo(() => ({
        Organic:  { label: t('contaminationRateChart.wasteTypes.Organic'),  color: STREAM_COLORS.Organic },
        PMD:      { label: t('contaminationRateChart.wasteTypes.PMD'),      color: STREAM_COLORS.PMD },
        Paper:    { label: t('contaminationRateChart.wasteTypes.Paper'),    color: STREAM_COLORS.Paper },
        Residual: { label: t('contaminationRateChart.wasteTypes.Residual'), color: STREAM_COLORS.Residual },
    }), [t])

    // ── Fetch ──────────────────────────────────────────────────────────────
    useEffect(() => {
        console.log("ContaminationRateChart fetch triggered", {
            isCustomDateRange,
            start: customDateRange?.[0]?.startDate,
            end: customDateRange?.[0]?.endDate,
        })

        if (!bins || bins.length === 0) return
        if (!timeRange && !isCustomDateRange) return

        setIsLoading(true)
        setError(null)
        setApiData(null)

        const params = new URLSearchParams()
        bins.forEach(b => params.append("bin", b))

        if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
            const s = customDateRange[0].startDate instanceof Date ? customDateRange[0].startDate : new Date(customDateRange[0].startDate)
            const e = customDateRange[0].endDate instanceof Date ? customDateRange[0].endDate : new Date(customDateRange[0].endDate)
            const toLocalDate = (d: Date) => {
                const amsterdamDate = new Date(d.toLocaleString("en-US", { timeZone: "Europe/Amsterdam" }))
                const pad = (n: number) => String(n).padStart(2, "0")
                return `${amsterdamDate.getFullYear()}-${pad(amsterdamDate.getMonth() + 1)}-${pad(amsterdamDate.getDate())}`
            }
            params.set("start_date", toLocalDate(s))
            params.set("end_date",   toLocalDate(e))
        } else {
            params.set("period", (timeRange || "year").replace(/\s+/g, "").toLowerCase())
        }

        axiosInstance
            .get(`/contamination-rates/?${params.toString()}`)
            .then(res => {
                if (res.data?.per_bin_data) setApiData(res.data)
                else setError(t('contaminationRateChart.errors.invalidDataFormat'))
            })
            .catch(() => setError(t('contaminationRateChart.errors.failedToLoad')))
            .finally(() => setIsLoading(false))
    }, [
        timeRange,
        isCustomDateRange,
        JSON.stringify(bins),
        customDateRange?.[0]?.startDate instanceof Date
            ? customDateRange[0].startDate.toDateString()
            : String(customDateRange?.[0]?.startDate ?? ""),
        customDateRange?.[0]?.endDate instanceof Date
            ? customDateRange[0].endDate.toDateString()
            : String(customDateRange?.[0]?.endDate ?? ""),
    ])
    // ── Build chart rows ────────────────────────────────────────────────────
    // One row per bin. Each stream column = contamination amount (% or kg).
    const chartRows = React.useMemo(() => {
        if (!apiData?.per_bin_data?.length) return []

        return apiData.per_bin_data.map(bin => {
            const row: Record<string, any> = { bin: bin.bin }

            STREAMS.forEach(stream => {
                const ws = bin.waste_streams.find(w => normalise(w.waste_stream) === stream)
                row[stream] = ws
                    ? showKg
                        ? (ws.contaminated_weight_kg ?? 0)
                        : (ws.contamination_rate ?? 0)
                    : 0
            })

            // store raw bin totals for tooltip
            row.__total_items              = bin.total_items
            row.__contaminated_items       = bin.contaminated_items
            row.__contamination_rate       = bin.contamination_rate
            row.__total_weight_kg          = bin.total_weight_kg
            row.__contaminated_weight_kg   = bin.contaminated_weight_kg

            return row
        })
    }, [apiData, showKg])

    // Only render streams that have at least one non-zero value
    const activeStreams = STREAMS.filter(s =>
        chartRows.some(r => (r[s] as number) > 0)
    )

    const hasWeightData = !!(apiData?.total_weight_kg && apiData.total_weight_kg > 0)

    // ── Custom tooltip ──────────────────────────────────────────────────────
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null
        const bin = apiData?.per_bin_data.find(b => b.bin === label)

        return (
            <div className="rounded-lg border bg-background p-3 shadow-md text-sm min-w-[190px]">
                <p className="font-semibold mb-2">{label}</p>
                <div className="space-y-1">
                    {payload
                        .filter((e: any) => Number(e.value) > 0)
                        .map((entry: any, i: number) => (
                            <div key={i} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-1.5">
                                    <div
                                        className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                                        style={{ backgroundColor: entry.fill }}
                                    />
                                    <span className="text-muted-foreground">{entry.name}</span>
                                </div>
                                <span className="font-medium">
                                    {showKg
                                        ? `${Number(entry.value).toFixed(2)} kg`
                                        : `${Number(entry.value).toFixed(1)}%`}
                                </span>
                            </div>
                        ))}
                </div>
                {bin && (
                    <div className="border-t mt-2 pt-2 text-xs text-muted-foreground space-y-0.5">
                        <div>
                            {t('contaminationRateChart.tooltip.totalItems')}:{" "}
                            <span className="font-medium text-foreground">{bin.total_items}</span>
                        </div>
                        <div>
                            {t('contaminationRateChart.tooltip.contaminated')}:{" "}
                            <span className="font-medium text-foreground">{bin.contaminated_items}</span>
                        </div>
                        <div>
                            {t('contaminationRateChart.tooltip.overallRate')}:{" "}
                            <span className="font-medium text-foreground">
                                {showKg && bin.total_weight_kg > 0
                                    ? `${bin.contamination_rate_by_weight?.toFixed(1) ?? "—"}%`
                                    : `${bin.contamination_rate.toFixed(1)}%`}
                            </span>
                        </div>
                        {showKg && bin.total_weight_kg > 0 && (
                            <div>
                                {t('contaminationRateChart.tooltip.totalWeight')}:{" "}
                                <span className="font-medium text-foreground">
                                    {bin.total_weight_kg.toFixed(2)} kg
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    // ── Exports ─────────────────────────────────────────────────────────────
    const handleExportCSV = () => {
        if (!apiData?.per_bin_data) return
        const headers = [
            "Bin",
            ...STREAMS.map(s => `${s} Contamination (%)`),
            ...STREAMS.map(s => `${s} Contaminated Weight (kg)`),
            "Total Items",
            "Contaminated Items",
            "Overall Contamination Rate (%)",
            "Correctly Sorted",
        ]
        const rows = apiData.per_bin_data.map(bin => {
            const pct = STREAMS.map(s => {
                const ws = bin.waste_streams.find(w => normalise(w.waste_stream) === s)
                return ws ? ws.contamination_rate.toFixed(2) : "0"
            })
            const kg = STREAMS.map(s => {
                const ws = bin.waste_streams.find(w => normalise(w.waste_stream) === s)
                return ws ? (ws.contaminated_weight_kg ?? 0).toFixed(3) : "0"
            })

            const correctlySorted = bin.contamination_rate === 0
                ? "Yes"
                : bin.contamination_rate === 100
                    ? "No"
                    : `Partial (${(100 - bin.contamination_rate).toFixed(1)}% sorted correctly)`

            return [
                bin.bin,
                ...pct,
                ...kg,
                bin.total_items,
                bin.contaminated_items,
                bin.contamination_rate.toFixed(2),
                correctlySorted,
            ].join(",")
        })
        const csv = [headers.join(","), ...rows].join("\n")
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = `contamination_comparison_${new Date().toISOString().slice(0, 10)}.csv`
        link.click()
    }

    const handleExportPNG = () => {
        if (!exportRef.current) return
        const controls = exportRef.current.querySelector(".export-controls") as HTMLElement | null
        if (controls) controls.style.display = "none"
        import("html2canvas").then(h => {
            h.default(exportRef.current!, { scale: 2, useCORS: true }).then(canvas => {
                const link = document.createElement("a")
                link.download = `contamination_comparison_${timeRange || "data"}.png`
                link.href = canvas.toDataURL("image/png")
                link.click()
            }).finally(() => { if (controls) controls.style.display = "" })
        })
    }

    // ── Dynamic chart height ─────────────────────────────────────────────────
    const barSize    = Math.min(34, Math.max(16, 180 / Math.max(chartRows.length, 1)))
    const chartHeight = Math.max(260, chartRows.length * (barSize + 22) + 70)

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            ref={exportRef}
            className={cn(
                "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section border-0 shadow-none",
                className
            )}
            {...props}
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="flex flex-wrap justify-between items-start gap-2 pb-2">
                <div>
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                        {title || t('contaminationRateChart.defaultTitle')}
                    </h4>
                    {isCustomDateRange &&
                        customDateRange[0]?.startDate &&
                        customDateRange[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-0.5">
                                {new Date(customDateRange[0].startDate!).toLocaleDateString()} –{" "}
                                {new Date(customDateRange[0].endDate!).toLocaleDateString()}
                            </p>
                        )}
                </div>

                <div className="flex items-center gap-2 export-controls">
                    {/* % / KG pill toggle */}
                    <div className="flex rounded-md border border-border overflow-hidden shrink-0">
                        <button
                            onClick={() => setShowKg(false)}
                            className={cn(
                                "px-3 py-1.5 text-xs font-semibold transition-colors",
                                !showKg
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background text-muted-foreground hover:bg-muted"
                            )}
                        >
                            %
                        </button>
                        <button
                            onClick={() => setShowKg(true)}
                            disabled={!hasWeightData}
                            className={cn(
                                "px-3 py-1.5 text-xs font-semibold transition-colors",
                                showKg
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background text-muted-foreground hover:bg-muted",
                                !hasWeightData && "opacity-40 cursor-not-allowed"
                            )}
                        >
                            KG
                        </button>
                    </div>

                    {/* Export dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-primary"
                                disabled={!apiData || isLoading}
                            >
                                <Download className="h-4 w-4 mr-1" />
                                {t('contaminationRateChart.export.export')}
                                <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportCSV}>{t('contaminationRateChart.export.downloadCSV')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportPNG}>{t('contaminationRateChart.export.downloadPNG')}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Help */}
                    <TooltipProvider>
                        <UITooltip>
                            <TooltipTrigger asChild>
                                <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[220px]">
                                <p dangerouslySetInnerHTML={{ __html: t('contaminationRateChart.help.description') }} />
                            </TooltipContent>
                        </UITooltip>
                    </TooltipProvider>
                </div>
            </div>

            {/* ── Chart body ───────────────────────────────────────────────── */}
            <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: '100%', height: '100%' }}>
                {isLoading ? (
                    <div className="flex items-center justify-center h-full w-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-destructive">
                        {error}
                    </div>
                ) : chartRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('contaminationRateChart.states.noDataAvailable')}</p>
                            <p className="text-sm">{t('contaminationRateChart.states.selectTimeRangeAndBins')}</p>
                        </div>
                    </div>
                ) : (
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer width="100%" height={100}>
                            <BarChart
                                data={chartRows}
                                layout="vertical"
                                margin={{ top: 1, right: 70, left: 10, bottom: 20 }}
                                stackOffset={showKg ? "none" : "expand"}
                            >
                                <CartesianGrid horizontal strokeDasharray="3 3" vertical={false} />

                                {/* Y-axis: bin names */}
                                <YAxis
                                    dataKey="bin"
                                    type="category"
                                    width={60}
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={10}
                                />

                                {/* X-axis: hidden like WasteComposition */}
                                <XAxis type="number" hide />

                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                                />

                                <Legend
                                    verticalAlign="bottom"
                                    iconType="square"
                                    iconSize={10}
                                    wrapperStyle={{ fontSize: 12, paddingTop: 14 }}
                                />

                                {/* One Bar per active stream — stacked */}
                                {activeStreams.map((stream, idx) => (
                                    <Bar
                                        key={stream}
                                        dataKey={stream}
                                        stackId="contamination"
                                        fill={STREAM_COLORS[stream]}
                                        name={chartConfig[stream].label}
                                        radius={
                                            idx === activeStreams.length - 1
                                                ? [0, 4, 4, 0]
                                                : [0, 0, 0, 0]
                                        }
                                    >
                                        {/* Label at end of last segment — total contamination */}
                                        {idx === activeStreams.length - 1 && (
                                            <LabelList
                                                dataKey={(entry: any) => {
                                                    if (showKg) {
                                                        const total = STREAMS.reduce(
                                                            (sum, s) => sum + (Number(entry[s]) || 0),
                                                            0
                                                        )
                                                        return `${total.toFixed(1)} kg`
                                                    }
                                                    // Overall contamination rate for this bin
                                                    return `${Number(entry.__contamination_rate || 0).toFixed(1)}%`
                                                }}
                                                position="right"
                                                offset={8}
                                                className="fill-foreground"
                                                fontSize={12}
                                            />
                                        )}
                                    </Bar>
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                )}
            </div>
        </div>
    )
}